import React, { useState, useEffect, useCallback } from "react";
import { ScrollText, RefreshCw, Plus } from "lucide-react";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../../components/loader/Loader";
import Modal from "../../../../components/modal/Modal";
import {
  getSecurityAccessLogs,
  createAccessLog,
  getAllAccessZones,
} from "../../../../service/securityService";
import type { AccessZone } from "../../../../types/security_types";

const AccessLogs: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [filter, setFilter] = useState<"All" | "Granted" | "Denied">("All");
  const [zones, setZones] = useState<AccessZone[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    accessType: "Granted" as "Granted" | "Denied",
    zone: "",
    userName: "",
    deviceId: "",
    reason: "",
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [logs, z] = await Promise.all([
        getSecurityAccessLogs({
          limit: 100,
          skip: 0,
          accessType:
            filter === "All" ? undefined : filter === "Granted" ? "Granted" : "Denied",
        }),
        getAllAccessZones(),
      ]);
      setRows(logs.data);
      setZones(z);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load access logs");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const submitLog = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createAccessLog({
        accessType: form.accessType,
        zone: form.zone || undefined,
        userName: form.userName || undefined,
        deviceId: form.deviceId || undefined,
        reason: form.reason || undefined,
      });
      toast.success("Access event recorded");
      setModalOpen(false);
      setForm({
        accessType: "Granted",
        zone: "",
        userName: "",
        deviceId: "",
        reason: "",
      });
      await load();
    } catch {
      toast.error("Could not record access event");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-2 py-4 w-full h-full mt-10 space-y-6">
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div className="flex items-center gap-3">
          <div className="bg-[#EAFBFA] p-3 rounded-lg">
            <ScrollText className="w-7 h-7 text-[#0fa9a6]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Access logs</h1>
            <p className="text-gray-600 text-sm max-w-2xl">
              Tenant access reader history. Physical systems should create rows
              through the authenticated access-log service (same pattern as this
              screen).
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            value={filter}
            onChange={(e) =>
              setFilter(e.target.value as "All" | "Granted" | "Denied")
            }
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value="All">All</option>
            <option value="Granted">Granted</option>
            <option value="Denied">Denied</option>
          </select>
          <button
            type="button"
            onClick={() => load()}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-full text-sm hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#0fa9a6] text-white rounded-full text-sm hover:bg-teal-600"
          >
            <Plus className="w-4 h-4" />
            Record event
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-100">
        {loading ? (
          <div className="flex justify-center py-16">
            <SpinLoader />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-gray-500 text-sm p-8 text-center">
            No access log rows yet.
          </p>
        ) : (
          <div className="overflow-x-auto max-h-[560px] overflow-y-auto text-sm">
            <table className="w-full text-left">
              <thead className="sticky top-0 bg-gray-100 text-xs">
                <tr>
                  <th className="p-2">Time</th>
                  <th className="p-2">Type</th>
                  <th className="p-2">Zone</th>
                  <th className="p-2">User / card</th>
                  <th className="p-2">Device</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={(r.id as string) || (r._id as string) || i} className="border-t">
                    <td className="p-2 whitespace-nowrap text-xs">
                      {r.accessDateTime
                        ? new Date(String(r.accessDateTime)).toLocaleString()
                        : "—"}
                    </td>
                    <td className="p-2">
                      <span
                        className={
                          r.accessType === "Denied"
                            ? "text-red-700 font-medium"
                            : "text-emerald-700 font-medium"
                        }
                      >
                        {String(r.accessType || "—")}
                      </span>
                    </td>
                    <td className="p-2">
                      {String(r.zone || r.location || "—")}
                    </td>
                    <td className="p-2">
                      {String(
                        r.userName || r.userId || r.cardId || "—"
                      )}
                    </td>
                    <td className="p-2 text-xs text-gray-600">
                      {String(r.deviceId || r.readerId || "—")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        closeButton={false}
        modalClass="bg-white w-full max-w-md rounded-xl shadow-lg relative flex flex-col"
      >
        <div className="p-5">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            Record access event
          </h2>
          <form onSubmit={submitLog} className="space-y-3 text-sm">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Outcome</label>
              <select
                className="w-full border rounded-lg px-3 py-2"
                value={form.accessType}
                onChange={(e) =>
                  setForm({
                    ...form,
                    accessType: e.target.value as "Granted" | "Denied",
                  })
                }
              >
                <option value="Granted">Granted</option>
                <option value="Denied">Denied</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Zone</label>
              <select
                className="w-full border rounded-lg px-3 py-2"
                value={form.zone}
                onChange={(e) => setForm({ ...form, zone: e.target.value })}
              >
                <option value="">Free text / optional</option>
                {zones.map((z) => {
                  const id = String(z._id || z.id || "");
                  return (
                    <option key={id || z.zoneName} value={id}>
                      {z.zoneName}
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                User name (optional)
              </label>
              <input
                className="w-full border rounded-lg px-3 py-2"
                value={form.userName}
                onChange={(e) =>
                  setForm({ ...form, userName: e.target.value })
                }
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Device / reader ID
              </label>
              <input
                className="w-full border rounded-lg px-3 py-2"
                value={form.deviceId}
                onChange={(e) =>
                  setForm({ ...form, deviceId: e.target.value })
                }
                placeholder="e.g. READER-01"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Reason (e.g. denied)
              </label>
              <input
                className="w-full border rounded-lg px-3 py-2"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 border rounded-lg text-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-[#0fa9a6] text-white rounded-lg disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
};

export default AccessLogs;
